import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

/**
 * Single-select scope filter (e.g. All / Frequent / Favorites on models).
 *
 * @param {{ id: string, label: string, icon: string, color?: string, iosIcon?: string }[]} options
 */
function CatalogScopeDropdown({
  options = [],
  value,
  onChange,
  defaultLabel = 'Scope',
  variant = 'default',
  style,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const isChip = variant === 'toolbarChip';
  const isNeutral = variant === 'neutral';

  const active = useMemo(
    () => options.find((o) => o.id === value) || options[0],
    [options, value],
  );

  const activeTint = isNeutral ? colors.text_color_primary : active?.color || colors.text_color_primary;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable
          style={({ hovered }) => [
            styles.trigger,
            isChip && styles.triggerChip,
            !isChip && isXS && styles.triggerCompact,
            isNeutral && { minHeight: isXS ? 42 : 32, paddingVertical: 0 },
            style,
            {
              backgroundColor: isNeutral
                ? (hovered ? colors.solid_color_tertiary : colors.solid_color_block)
                : isChip
                ? `${activeTint}12`
                : (hovered ? colors.solid_color_secondary_dark : colors.solid_color_secondary),
              borderColor: isNeutral ? colors.border_color_secondary : activeTint,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel={active?.label || defaultLabel}
        >
          <Icon
            name={active?.icon || 'grid-2'}
            size={13}
            color={activeTint}
            solid={value === 'favorites'}
          />
          <Text
            size={isChip ? 'xs' : (isXS ? 'xs' : 's')}
            weight="medium"
            numberOfLines={1}
            style={[
              { color: activeTint, maxWidth: isChip ? undefined : (isXS ? 88 : 120) },
              isChip && styles.triggerChipLabel,
            ]}
          >
            {active?.label || defaultLabel}
          </Text>
          <Icon name="chevron-down" size={10} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align={isChip ? 'center' : 'end'}
        sideOffset={4}
        style={{ minWidth: 200 }}
      >
        {options.map((opt) => (
          Platform.OS === 'web' ? (
            <DropdownMenuItem
              key={opt.id}
              icon={opt.icon}
              iosIcon={opt.iosIcon}
              title={opt.label}
              isActive={value === opt.id}
              onSelect={() => onChange?.(opt.id)}
            />
          ) : (
            // Native menus only read children whose element type is
            // DropdownMenu.Item, so the wrapper is web-only here.
            <DropdownMenu.Item key={opt.id} onSelect={() => onChange?.(opt.id)}>
              {(value === opt.id || opt.iosIcon) && (
                <DropdownMenu.ItemIcon ios={{ name: value === opt.id ? 'checkmark' : opt.iosIcon }} />
              )}
              <DropdownMenu.ItemTitle>{opt.label}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    flexShrink: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  triggerCompact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 5,
  },
  triggerChip: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: 36,
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRadius: 10,
  },
  triggerChipLabel: {
    flex: 1,
    minWidth: 0,
  },
});

export default React.memo(CatalogScopeDropdown);
